/** Stable, unlisted read capabilities. These tokens never authorize upload or deletion. */
export const RECORDINGS_ORIGIN = 'https://api.callsyncapp.com';
// Previously released clients and stored links still use these Worker hosts.
const managedOrigins = new Set([
  RECORDINGS_ORIGIN,
  'https://callsync-api-next.ygsbhardwaj.workers.dev',
  'https://callsync-android-next.ygsbhardwaj.workers.dev',
]);

export function managedRecordingKey(raw: string): string | null {
  let url: URL;
  try { url = new URL(raw); } catch { return null; }
  if (!managedOrigins.has(url.origin) || url.pathname !== '/v1/recordings/file') return null;
  return url.searchParams.get('key');
}

async function signingKey(secret: string, usage: 'sign' | 'verify'): Promise<CryptoKey> {
  if (!secret) throw new Error('Recording link signing is unavailable');
  return crypto.subtle.importKey('raw', new TextEncoder().encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [usage]);
}

export async function unlistedRecordingUrl(secret: string, key: string): Promise<string> {
  if (!/^[A-Za-z0-9_-]+\/\d+\/.+/.test(key) || key.includes('..') || key.includes('\\')) throw new Error('Invalid recording key');
  const signature = await crypto.subtle.sign('HMAC', await signingKey(secret, 'sign'), new TextEncoder().encode(`recording-read:v1:${key}`));
  const token = Array.from(new Uint8Array(signature), byte => byte.toString(16).padStart(2, '0')).join('');
  const url = new URL('/v1/recordings/file', RECORDINGS_ORIGIN);
  url.searchParams.set('key', key); url.searchParams.set('share', token);
  return url.toString();
}

/** Exact stored URL forms for one key; deletion never matches filenames or prefixes. */
export async function recordingUrlAliases(secret: string, key: string): Promise<string[]> {
  const shared = new URL(await unlistedRecordingUrl(secret, key));
  const token = shared.searchParams.get('share')!;
  const links = new Set<string>([key]);
  for (const origin of managedOrigins) {
    const canonical = new URL('/v1/recordings/file', origin);
    canonical.searchParams.set('key', key);
    links.add(canonical.toString());
    canonical.searchParams.set('share', token);
    links.add(canonical.toString());
    // Older producers used encodeURIComponent instead of URLSearchParams.
    const encoded = `${origin}/v1/recordings/file?key=${encodeURIComponent(key)}`;
    links.add(encoded);
    links.add(`${encoded}&share=${token}`);
  }
  const legacy = `https://r2.callsyncapp.com/${key}`;
  links.add(legacy);
  links.add(new URL(legacy).toString());
  links.add(`https://r2.callsyncapp.com/${key.split('/').map(encodeURIComponent).join('/')}`);
  return [...links];
}

export async function verifyRecordingShare(secret: string, key: string, token: string): Promise<boolean> {
  if (!secret || !/^[a-f0-9]{64}$/.test(token)) return false;
  const signature = Uint8Array.from(token.match(/../g)!, byte => parseInt(byte, 16));
  return crypto.subtle.verify('HMAC', await signingKey(secret, 'verify'), signature, new TextEncoder().encode(`recording-read:v1:${key}`));
}

export async function shareWorkspaceRecording(secret: string, raw: string | null, orgId: string): Promise<string | null> {
  if (!raw) return null;
  const key = managedRecordingKey(raw);
  if (!key) return raw; // Existing external/fixture media keep their own access contract.
  if (!key.startsWith(orgId + '/')) throw new Error('Recording belongs to another workspace');
  return unlistedRecordingUrl(secret, key);
}

/** Publish media URLs inside CRM timeline records as well as call-list records. */
export async function shareRecordingFields(secret: string, value: unknown, orgId: string): Promise<unknown> {
  if (Array.isArray(value)) return Promise.all(value.map(item => shareRecordingFields(secret, item, orgId)));
  if (!value || typeof value !== 'object') return value;
  const entries = await Promise.all(Object.entries(value).map(async ([field, item]) => [field,
    ['recording', 'recording_path', 'recording_url'].includes(field) && typeof item === 'string'
      ? await shareWorkspaceRecording(secret, item, orgId)
      : await shareRecordingFields(secret, item, orgId)
  ]));
  return Object.fromEntries(entries);
}
